/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { css, cx } from "@emotion/css";
import { space } from "@octopusdeploy/design-system-tokens";
import debounce from "lodash.debounce";
import { useLayoutEffect, useState, type ReactElement, type ReactNode } from "react";
import * as React from "react";
import { useIsLargerThanIpadResolution } from "../../hooks";
import type { DesignSystemLinkHref } from "../../routing";
import type { BreadcrumbItem } from "../Breadcrumbs";
import { PageHeaderPrimary, type PageHeaderPrimaryLogo } from "../PageHeaderPrimary";
import { useSetInPageNavVisible } from "./InPageNavVisibilityContext";
import type { MenuNodeItem } from "./MenuNode";
import type { PageSideNavItem, PageSideNavLink } from "./PageSideNav";
import { isPageSideNavLink, PageSideNav } from "./PageSideNav";
import PriorityNavigation from "./PriorityNavigation";
interface Level2OuterPageLayoutProps {
    /**
     * Page Navigation items to be displayed in the sidebar.
     * Refer to the PageSideNav component for more information.
     */
    navItems?: PageSideNavItem[];
    children: ReactNode;
    /**
     * Defines the elements used in the page header, ensuring a consistent and structured layout.
     * The `title` is required, while other elements such as chips, breadcrumbs, and actions are optional.
     * - **breadcrumbs**: `BreadcrumbItem[]` *(optional)*
     * An array of breadcrumb items for hierarchical navigation. Refer to the the Breadcumbs component.
     * - **logo**: `PageHeaderPrimaryLogo` *(optional)*
     * The primary logo element to be displayed within the header.
     * - **title**: `string` *(required)*
     * The main title of the page. This is a required field and serves as the primary header text.
     * - **logoLinkURL**: `DesignSystemLinkHref` *(optional)*
     * A URL associated with the logo, allowing navigation when clicked.
     * - **titleIcon**: `React.ReactElement` *(optional)*
     * An optional icon to be displayed alongside the page title.
     * - **titleChips**: `React.ReactElement[]` *(optional)*
     * A list of chip component to display alongside the title, often used for status or categorization. Refer to the Chip component.
     * - **statusSection**: `React.ReactNode` *(optional)*
     * Displays the status of the current page, such as a project status
     */
    header?: {
        breadcrumbs?: BreadcrumbItem[];
        logo?: PageHeaderPrimaryLogo;
        title: string;
        logoLinkURL?: DesignSystemLinkHref;
        titleIcon?: ReactElement;
        titleChips?: ReactElement[];
        statusSection?: ReactNode;
    };
    /**
     * Displays important information or alerts to the user.
     * Please refer to the Callout component for more information.
     */
    callout?: ReactNode; //TODO: Update type to be the same as Level1PageLayout
    /**
     * Displays the progress of a tour or walkthrough for the user. Visible in the sidebar navigation.
     */
    tourProgress?: ReactNode;
    /**
     * Adds a description to the page layout visible in the sidebar navigation, providing context or additional information to the user.
     *
     * This needs design review.
     */
    description?: ReactNode;
}
// This value is chosen as the minimum width where the Process Editor doesn't feel cramped with the sidenav
const minimumWidthToFitSideNav = 1050;
export function Level2OuterPageLayout({ navItems = [], children, description, header, callout, tourProgress }: Level2OuterPageLayoutProps) {
    const setIsInPageNavVisible = useSetInPageNavVisible();
    const containerRef = React.useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState<number>();
    useLayoutEffect(() => {
        if (!containerRef.current)
            return;
        setContainerWidth(containerRef.current.clientWidth);
        function onContainerResize(entries: ResizeObserverEntry[]) {
            const containerWidth = entries[0].contentRect.width;
            setContainerWidth(containerWidth);
        }
        const containerResizeObserver = new ResizeObserver(debounce(onContainerResize, 100, { leading: true }));
        containerResizeObserver.observe(containerRef.current);
        return () => containerResizeObserver.disconnect();
    }, [containerRef]);
    const isLargerThanIpad = useIsLargerThanIpadResolution();
    const hasItems = navItems.length > 0;
    React.useEffect(() => {
        setIsInPageNavVisible?.(hasItems);
        return () => setIsInPageNavVisible?.(false);
    }, [hasItems, setIsInPageNavVisible]);
    const sideNavView = (<div className={sideBarLayoutStyles}>
            {hasItems && <PageSideNav items={navItems} tourProgress={tourProgress} description={description}/>}
            <div className={cx(sideBarLayoutContentStyles, {
            [sideBarLayoutContentNoSidebarStyles]: !hasItems,
        })} key="content">
                {children}
            </div>
        </div>);
    const topNavView = (<div className={sideBarLayoutContentMobileStyles}>
            <div className={isLargerThanIpad ? resumeTourButtonIpadContainerStyles : resumeTourButtonMobileContainerStyles}>{tourProgress}</div>
            {hasItems && (<div>
                    <PriorityNavigation maxNavigationItems={99} navigationItems={navItems.filter(isPageSideNavLink).map(convertNavLinkToMenuNodeItem)}/>
                </div>)}
            <div key="content">{children}</div>
        </div>);
    return (<div className={layoutContainerStyles} ref={containerRef}>
            <div className={contentContainerStyles}>
                {header && <PageHeaderPrimary {...header}/>}
                {callout}
                {containerWidth !== undefined && containerWidth >= minimumWidthToFitSideNav ? sideNavView : topNavView}
            </div>
        </div>);
}
function convertNavLinkToMenuNodeItem(navLink: PageSideNavLink): MenuNodeItem {
    return { url: navLink.path, text: navLink.label as string, exact: navLink.exact, onClick: navLink.onClick };
}
const layoutContainerStyles = css({
    flex: 1,
    display: "flex",
    justifyContent: "center",
});
const contentContainerStyles = css({
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
});
const sideBarLayoutStyles = css({
    flex: 1,
    display: "flex",
});
const sideBarLayoutContentStyles = css({
    flex: "1",
    padding: `${space[16]} ${space[32]} ${space[16]} 0`,
    // https://www.w3.org/TR/css-flexbox-1/#min-size-auto
    // This prevents the child from growing larger than the parent flexbox container if the childs content is large
    minWidth: 0,
});
const sideBarLayoutContentNoSidebarStyles = css({
    padding: `${space[16]} ${space[32]} ${space[16]} ${space[32]}`,
});
const sideBarLayoutContentMobileStyles = css({
    display: "flex",
    flexDirection: "column",
    padding: `0 ${space[16]}`,
});
const resumeTourButtonIpadContainerStyles = css({
    maxWidth: 254,
    padding: `0 0 ${space[16]} ${space[16]}`,
});
const resumeTourButtonMobileContainerStyles = css({
    maxWidth: 254,
    padding: `0 0 ${space[16]} 0`,
});
